import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  UI_LOCALES,
  LOCALE_ENDONYMS,
  isLocale,
  storedLocale,
  rememberLocale,
  resolveLocale,
  loadCatalogue,
  isLoaded,
  translate,
  type Locale,
} from "./i18n";

interface LocaleContextValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  applyAccountDefaults: (orgLocale?: string | null, userLocale?: string | null) => void;
  locales: readonly Locale[];
  endonyms: Record<Locale, string>;
  t: (key: string, vars?: Record<string, unknown>) => string;
  _generation: number;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

/** Wraps the whole SPA, above everything, so marketing pages, the public
 *  rental flow and the tenant portal get a language too — all three are read
 *  by people who have not signed in and whose operator we may not know yet.
 *
 *  The chosen locale also lands on `<html lang>`, which is not cosmetic: it is
 *  what a screen reader picks a voice from and what the browser hyphenates by.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => resolveLocale());
  // Bumped when a catalogue finishes loading, so every `t` re-renders with the
  // real strings instead of the English it answered with meanwhile.
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = locale;
    if (isLoaded(locale)) return;
    let live = true;
    loadCatalogue(locale).then(() => {
      if (live) setGeneration((g) => g + 1);
    });
    return () => {
      live = false;
    };
  }, [locale]);

  /** A deliberate choice by the person in front of the screen. Sticky. */
  const setLocale = useCallback((next: Locale) => {
    if (!isLocale(next)) return;
    rememberLocale(next);
    setLocaleState(next);
  }, []);

  /** The operator's and the account's defaults, applied once known.
   *
   *  An explicit choice always wins — checking `storedLocale()` here rather
   *  than trusting state is what stops a page refresh quietly reverting
   *  somebody to their operator's language after they picked their own.
   */
  const applyAccountDefaults = useCallback((orgLocale?: string | null, userLocale?: string | null) => {
    if (storedLocale()) return;
    const next = resolveLocale({ orgLocale, userLocale });
    setLocaleState((cur) => (next === cur ? cur : next));
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
      applyAccountDefaults,
      locales: UI_LOCALES,
      endonyms: LOCALE_ENDONYMS,
      // `generation` is read here so the memo (and therefore every consumer)
      // recomputes when a catalogue lands. Without it the first paint after a
      // language switch stays English until something else re-renders.
      t: (key: string, vars?: Record<string, unknown>) => translate(locale, key, vars),
      _generation: generation,
    }),
    [locale, setLocale, applyAccountDefaults, generation],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (ctx) return ctx;
  // A component rendered outside the provider (a test, a stray island) still
  // has to render. English with no switcher is the right degradation; throwing
  // would turn a wiring mistake into a blank page.
  return {
    locale: "en",
    setLocale: () => {},
    applyAccountDefaults: () => {},
    locales: UI_LOCALES,
    endonyms: LOCALE_ENDONYMS,
    t: (key: string, vars?: Record<string, unknown>) => translate("en", key, vars),
    _generation: 0,
  };
}

/** The common case: `const t = useT()` then `t('nav.today')`. */
export function useT() {
  return useLocale().t;
}
